<div class="flex flex-col gap-4">
  <!-- Page Header -->
  <div class="flex justify-between items-center flex-wrap gap-3">
    <div class="flex-1">
      <h1 class="text-gray-900 m-0 mb-1 text-2xl font-semibold">Plugin Releases & Downloads</h1>
      <p class="text-gray-500 m-0 text-lg">Manage plugin versions, cloud deployment, and track installation statistics.</p>
    </div>
    <div class="flex gap-2 items-center">
      @if (activeView === 'releases' && canManageReleases) {
        <p-button
          label="Upload New Release"
          icon="pi pi-upload"
          (onClick)="openCreateDialog()">
        </p-button>
      }
    </div>
  </div>

  <!-- View Mode Switcher -->
  <div class="flex gap-2">
    @for (option of viewOptions; track option.value) {
      <p-button
        [label]="option.label"
        [icon]="option.icon"
        [severity]="activeView === option.value ? 'primary' : 'secondary'"
        (onClick)="setView(option.value)">
      </p-button>
    }
  </div>

  <!-- TAB 1: Releases Table -->
  @if (activeView === 'releases') {
    @if (!loading) {
      <div>
        <p-table
          [value]="releases"
          [loading]="loading">
          <ng-template #header>
            <tr>
              <th>Version</th>
              <th>Release Date</th>
              <th>Status</th>
              <th>File Size</th>
              <th>Downloads</th>
              <th class="text-center">Actions</th>
            </tr>
          </ng-template>
          <ng-template #body let-release>
            <tr>
              <td>
                <div class="font-semibold">{{ release.version }}</div>
                <div class="text-sm text-gray-500">{{ release.fileName }}</div>
              </td>
              <td>{{ release.createdAt | date:'medium' }}</td>
              <td>
                <p-tag
                  [value]="getStatusText(release.isPublished)"
                  [severity]="getStatusSeverity(release.isPublished)"
                  [rounded]="true">
                </p-tag>
              </td>
              <td>{{ formatFileSize(release.fileSize) }}</td>
              <td>
                <button
                  type="button"
                  class="cursor-pointer border-none bg-transparent p-0 hover:opacity-80 transition-opacity"
                  (click)="filterByVersion(release.version)"
                  pTooltip="Click to view downloads log for this version">
                  <p-tag
                    [value]="(release.downloadCount || 0) + ' downloads'"
                    [severity]="(release.downloadCount || 0) > 0 ? 'info' : 'secondary'"
                    [rounded]="true"
                    icon="pi pi-download">
                  </p-tag>
                </button>
              </td>
              <td>
                <div class="flex gap-2 justify-center">
                  <p-button
                    icon="pi pi-eye"
                    severity="info"
                    variant="text"
                    pTooltip="View Notes"
                    (onClick)="openNotesDialog(release)"
                    aria-label="View release notes">
                  </p-button>
                  @if (canManageReleases) {
                    <p-button
                      icon="pi pi-pencil"
                      severity="secondary"
                      variant="text"
                      pTooltip="Edit"
                      (onClick)="openEditDialog(release)"
                      aria-label="Edit release">
                    </p-button>
                  }
                  @if (canManageReleases && !release.isPublished) {
                    <p-button
                      icon="pi pi-cloud-upload"
                      severity="success"
                      variant="text"
                      pTooltip="Publish to Cloud"
                      (onClick)="publishRelease(release)"
                      aria-label="Publish release">
                    </p-button>
                  }
                  @if (canManageReleases && release.isPublished) {
                    <p-button
                      icon="pi pi-cloud"
                      severity="warn"
                      variant="text"
                      pTooltip="Unpublish from Cloud"
                      (onClick)="unpublishRelease(release)"
                      aria-label="Unpublish release">
                    </p-button>
                  }
                  @if (canManageReleases && canRollbackTo(release)) {
                    <p-button
                      icon="pi pi-history"
                      severity="danger"
                      variant="text"
                      pTooltip="Rollback to this version"
                      (onClick)="rollbackToRelease(release)"
                      aria-label="Rollback to this version">
                    </p-button>
                  }
                  <p-button
                    icon="pi pi-download"
                    severity="info"
                    variant="text"
                    pTooltip="Download File"
                    (onClick)="downloadRelease(release)"
                    aria-label="Download release file">
                  </p-button>
                  @if (canManageReleases) {
                    <p-button
                      icon="pi pi-trash"
                      severity="danger"
                      variant="text"
                      pTooltip="Delete Release"
                      (onClick)="deleteRelease(release)"
                      aria-label="Delete release">
                    </p-button>
                  }
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="6" class="text-center">
                <div class="p-4">
                  <i class="pi pi-upload text-4xl text-gray-400 mb-3"></i>
                  <h3 class="text-gray-600 mb-2">No Releases Found</h3>
                  <p class="text-gray-500">Upload your first release to get started</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    } @else {
      <div class="flex justify-center items-center p-4">
        <p-progress-spinner></p-progress-spinner>
      </div>
    }
  }

  <!-- TAB 2: Downloads History Component -->
  @if (activeView === 'downloads') {
    <app-releases-downloads
      [versionFilterOptions]="versionFilterOptions"
      [initialVersion]="selectedDownloadVersion">
    </app-releases-downloads>
  }

  <!-- Create Release Dialog -->
  <p-dialog
    header="Upload New Release"
    [(visible)]="showCreateDialog"
    [modal]="true"
    [style]="{width: '600px'}"
    [closable]="true">

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label for="version" class="font-semibold">Version <span class="text-red-500">*</span></label>
        <input
          pInputText
          id="version"
          [(ngModel)]="createForm.version"
          (ngModelChange)="checkVersionExists($event)"
          placeholder="e.g. 2.0.0 or 2.0.0.0"
          class="w-full"
          [class.border-red-500]="versionExists" />
        @if (!versionExists && createForm.version) {
          <small class="text-gray-500">Version detected from file or entered manually</small>
        }
        @if (versionExists) {
          <small class="text-red-500">⚠️ This version already exists!</small>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="file" class="font-semibold">Release File <span class="text-red-500">*</span></label>
        @if (showCreateDialog) {
          <p-fileupload
            mode="basic"
            chooseLabel="Choose File"
            accept=".msi,.exe"
            [maxFileSize]="100000000"
            (onSelect)="onFileSelect($event)"
            [auto]="false">
          </p-fileupload>
        }
        <small class="text-gray-500">Supported formats: MSI, EXE (max 100MB)</small>
      </div>

      <div class="flex flex-col gap-2">
        <label for="releaseNotes" class="font-semibold">
          Release Notes
          <span class="text-red-500">*</span>
        </label>
        <textarea
          pTextarea
          id="releaseNotes"
          [(ngModel)]="createForm.releaseNotes"
          placeholder="What's new in this version..."
          rows="6"
          class="w-full"
          required>
        </textarea>
        <small class="text-gray-500">Required field</small>
      </div>
    </div>

    <ng-template #footer>
      <div class="flex gap-2 justify-end">
        <p-button
          label="Cancel"
          severity="secondary"
          [disabled]="creating"
          (onClick)="closeCreateDialog()">
        </p-button>
        <p-button
          [label]="creating ? 'Creating...' : 'Create Release'"
          [loading]="creating"
          [disabled]="creating || versionExists || !uploadedFile || !createForm.releaseNotes.trim()"
          (onClick)="createRelease()">
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Edit Release Dialog -->
  <p-dialog
    header="Edit Release"
    [(visible)]="showEditDialog"
    [modal]="true"
    [style]="{width: '500px'}"
    [closable]="true">

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label for="editVersion" class="font-semibold">Version</label>
        <input
          pInputText
          id="editVersion"
          [(ngModel)]="editForm.version"
          class="w-full" />
      </div>

      <div class="flex flex-col gap-2">
        <label for="editReleaseNotes" class="font-semibold">Release Notes</label>
        <textarea
          pTextarea
          id="editReleaseNotes"
          [(ngModel)]="editForm.releaseNotes"
          rows="6"
          class="w-full">
        </textarea>
      </div>
    </div>

    <ng-template #footer>
      <div class="flex gap-2 justify-end">
        <p-button
          label="Cancel"
          severity="secondary"
          (onClick)="showEditDialog = false">
        </p-button>
        <p-button
          label="Save Changes"
          [loading]="updating"
          [disabled]="updating"
          (onClick)="updateRelease()">
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- View Notes Dialog -->
  <p-dialog
    header="Release Notes"
    [(visible)]="showNotesDialog"
    [modal]="true"
    [style]="{width: '600px'}"
    [closable]="true">

    @if (selectedRelease) {
      <div class="flex flex-col gap-4">
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="text-lg font-semibold m-0 mb-2">Version {{ selectedRelease.version }}</h3>
          <p class="text-sm text-gray-600 m-0">Released on {{ selectedRelease.createdAt | date:'fullDate' }}</p>
        </div>
        <div class="whitespace-pre-wrap">
          {{ selectedRelease.releaseNotes || 'No release notes available.' }}
        </div>
      </div>
    }

    <ng-template #footer>
      <div class="flex gap-2 justify-end">
        <p-button
          label="Close"
          (onClick)="showNotesDialog = false">
        </p-button>
      </div>
    </ng-template>
  </p-dialog>
</div>

<p-confirmdialog></p-confirmdialog>
<p-toast></p-toast>
